package tools

import io.ktor.http.*
import io.ktor.util.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.coroutines.withTimeoutOrNull
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.asList
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.js.*
import kotlin.time.Duration
import kotlin.time.Duration.Companion.minutes


@Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
actual object FilePicker {

    actual suspend fun uploadFromPicker(
        contentTypes: List<String>,
        pickerTimeout: Duration,
        params: FilePickerUploadParams,
        multiple: Boolean,
        onNewFile: suspend (json: String) -> Unit
    ): Flow<FilePickerUploadState> {
        val files = withTimeoutOrNull(pickerTimeout) {
            pickFiles(contentTypes, multiple)
        }

        return flow {
            if (files.isNullOrEmpty()) emit(FilePickerUploadState(0))
            else {
                var state = FilePickerUploadState(files.size)
                emit(state)
                files.forEach { file ->
                    runCatching {
                        uploadFile(file, params)
                    }.onSuccess {
                        onNewFile(it)
                        state = state.copy(success = state.success + 1)
                    }.onFailure {
                        //NotAcceptable

                        state = if (it is StatusCodeErr && it.code.value == HttpStatusCode.PayloadTooLarge.value) {
                            state.copy(
                                failure = state.failure + 1,
                                tooLargeFiles = state.tooLargeFiles + 1
                            )
                        } else if (it is StatusCodeErr && it.code.value == HttpStatusCode.NotAcceptable.value) {
                            state.copy(
                                failure = state.failure + 1,
                                invalidCustomVideoFormat = state.invalidCustomVideoFormat + 1
                            )
                        } else {
                            state.copy(failure = state.failure + 1)
                        }
                        it.printStackTrace()
                    }
                    emit(state)
                }
            }
        }
    }

    private suspend fun uploadFile(file: File, params: FilePickerUploadParams): String {

        val result = window.fetch(
            input = params.buildUrl(),
            init = RequestInit(
                method = "PUT",
                headers = json(*params.buildHeaders(file.name, file.type)),
                body = file
            )
        ).await()

        return with(result) {
            val state = HttpStatusCode(status.toInt(), statusText)
            if (!state.isSuccess()) state.error()
            text().await()
        }
    }

    private suspend fun pickFiles(contentTypes: List<String>, multiple: Boolean = true): List<File>? = suspendCancellableCoroutine { cont ->
        var canContinue = true
        cont.invokeOnCancellation {
            canContinue = false
        }
        val inputElement = document.createElement("input") as HTMLInputElement
        inputElement.type = "file"
        inputElement.multiple = multiple
        inputElement.accept = contentTypes.joinToString(",")
        inputElement.oncancel = {
            if (cont.isActive && canContinue) {
                canContinue = false
                cont.resume(null)
            }
        }
        inputElement.onchange = {
            if (cont.isActive && canContinue) {
                canContinue = false
                runCatching {
                    cont.resume(
                        inputElement.files
                            ?.asList()
                            ?.also {
                                println("TYPES: ${it.map { it.type }}")
                            }
                            ?.filter { it.type in contentTypes })
                }
            }
        }
        inputElement.click()
    }

    actual suspend fun single(contentType: String, onStart: suspend (name: String) -> Unit): VirtualFile? {
        val enabledTypes = contentType.split(",")
            .map { it.trim() }
            .filter { it.isNotEmpty() }
        return withTimeoutOrNull(5.minutes) {
            invokePickerSingle(contentType)
                .await()
                .also { onStart(it.name) }
                .takeIf { enabledTypes.contains("*/*") || it.type in enabledTypes }
                ?.let {
                    VirtualFile(
                        name = it.name,
                        bytes = it.readByteArray() ?: byteArrayOf(),
                        mime = it.type
                    )
                }
        }
    }

    @Suppress("UnsafeCastFromDynamic")
    private fun invokePickerMultiple(
        @Suppress("UNUSED_PARAMETER") contentType: String,
    ): Promise<Array<File>> {
        return js(
            """
             var input = document.createElement('input');
             input.type = 'file';
             input.multiple = true;
             input.accept = contentType;
               
             new Promise(function (resolve) {
                 input.onchange = function () {
                    var files = Array.from(input.files);
                     resolve(files);
                 };    
                 input.click();
            });
            """
        )
    }

    @Suppress("UnsafeCastFromDynamic")
    private fun invokePickerSingle(
        @Suppress("UNUSED_PARAMETER") contentType: String,
    ): Promise<File> {
        return js(
            """
             var input = document.createElement('input');
             input.type = 'file';
             input.multiple = false;
             input.accept = contentType;
               
             new Promise(function (resolve) {
                 input.onchange = function () {
                    var files = Array.from(input.files);
                    resolve(files[0]);
                 };    
                 input.click();
            });
            """
        )
    }

    suspend fun File.readByteArray(): ByteArray? = suspendCancellableCoroutine { cont ->
        val reader = FileReader()
        reader.onabort = {
            cont.resume(null)
        }
        reader.onerror = {
            cont.resume(null)
        }
        reader.onload = {
            val buff = reader.result as? ArrayBuffer
            cont.resume(buff?.toByteArray())
            it
        }
        reader.readAsArrayBuffer(this)
    }

    fun ArrayBuffer.toByteArray(): ByteArray = Int8Array(this).unsafeCast<ByteArray>()

    actual suspend fun uploadBinary(data: ByteArray, mime: String, name: String) {

        val base64 = data.encodeBase64()

        val element = document.createElement("a").apply {
            setAttribute("href", "data:$mime;base64,$base64")
            setAttribute("download", name)
        }

        document.body?.appendChild(element)
        element.asDynamic().click()
        document.body?.removeChild(element)
    }
}
